<template>
  <button :type="type" :disabled="disabled || loading" :class="buttonClass" @click="handleClick">
    <span v-if="loading" class="flex items-center gap-2">
      <svg
        class="w-4 h-4 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <span v-if="loadingText">{{ loadingText }}</span>
    </span>
    <span v-else>
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'text'
  size?: 'small' | 'default' | 'large'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'default',
  type: 'button',
  disabled: false,
  loading: false,
  fullWidth: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClass = computed(() => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-xl font-medium cursor-pointer transition-all duration-200 font-sans no-underline disabled:opacity-60 disabled:cursor-not-allowed'

  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-bg-hover',
    secondary: 'bg-secondary text-white hover:bg-blue',
    danger: 'bg-red text-white hover:bg-red-hover',
    outline:
      'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white',
    text: 'bg-transparent text-primary hover:text-bg-hover underline',
  }

  const sizeClasses = {
    small: 'px-4 py-2 text-sm',
    default: 'px-6 py-3 text-sm',
    large: 'px-8 py-4 text-sm',
  }

  return [
    baseClasses,
    variantClasses[props.variant],
    sizeClasses[props.size],
    props.fullWidth ? 'w-full' : '',
  ]
    .filter(Boolean)
    .join(' ')
})

const handleClick = (event: MouseEvent): void => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>
